import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolves either a full https URL or a backend-relative /api/files/... path to a usable img src.
// Optional width `w` appends ?w=<px> so the server returns a resized, format-optimized variant.
export const resolveImgUrl = (u, w) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  const q = w ? `${u.includes("?") ? "&" : "?"}w=${w}` : "";
  return `${BACKEND_URL}${u}${q}`;
};

// Builds a responsive srcset string for backend-served images so cards request smaller sizes.
// Returns undefined for external URLs (can't be optimized) so React omits the attribute.
export const imgSrcSet = (u, widths = [200, 400, 600, 800]) => {
  if (!u || u.startsWith("http://") || u.startsWith("https://")) return undefined;
  return widths.map((w) => `${resolveImgUrl(u, w)} ${w}w`).join(", ");
};

export default function ImageUploader({ value = [], onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    const arr = Array.from(files || []).slice(0, 6);
    if (arr.length === 0) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of arr) {
        if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} is over 5MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data.url);
      }
      onChange?.([...(value || []), ...uploaded]);
      toast.success(`${uploaded.length} image(s) uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (i) => {
    const next = value.filter((_, idx) => idx !== i);
    onChange?.(next);
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        onClick={() => !busy && inputRef.current?.click()}
        className="border-2 border-dashed border-slate-200 hover:border-pink-300 hover:bg-pink-50/30 rounded-2xl p-6 text-center cursor-pointer transition-colors"
        data-testid="image-uploader"
      >
        {busy ? (
          <div className="flex items-center justify-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin"/>Uploading…</div>
        ) : (
          <>
            <Upload className="w-7 h-7 mx-auto text-slate-400"/>
            <div className="text-sm font-bold mt-2 text-slate-700">Click to upload or drag & drop</div>
            <div className="text-xs text-slate-500 mt-1">JPG, PNG, WEBP — max 5MB · select multiple</div>
          </>
        )}
        <input
          ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp"
          className="hidden" onChange={(e) => handleFiles(e.target.files)}
          data-testid="image-upload-input"
        />
      </div>

      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {value.map((u, i) => (
            <div key={i} className="relative group aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
              <img src={resolveImgUrl(u)} alt="" className="w-full h-full object-cover"/>
              {i === 0 && <span className="absolute top-1 left-1 bg-pink-500 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full">Main</span>}
              <button type="button" onClick={() => remove(i)} data-testid={`remove-img-${i}`}
                className="absolute top-1 right-1 w-6 h-6 bg-white rounded-full shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 hover:text-white">
                <X className="w-3.5 h-3.5"/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Single-image picker — for hero banners, category tiles, etc.
export function SingleImagePicker({ value, onChange, label, hint, aspect = "aspect-[4/3]", testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.(r.data.url);
      toast.success("Image updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      {label && <div className="text-xs font-bold text-slate-700 mb-1.5">{label}</div>}
      <div className={`relative ${aspect} rounded-xl overflow-hidden border-2 border-dashed border-slate-200 bg-slate-50 group hover:border-pink-300 transition-colors`}>
        {value ? (
          <>
            <img src={resolveImgUrl(value)} alt="" className="absolute inset-0 w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
                className="bg-white rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1 hover:bg-pink-500 hover:text-white transition-colors">
                {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Upload className="w-3 h-3"/>}
                Replace
              </button>
              <button type="button" onClick={() => onChange?.("")}
                className="bg-white rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1 hover:bg-red-500 hover:text-white transition-colors">
                <X className="w-3 h-3"/>Remove
              </button>
            </div>
          </>
        ) : (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
            className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-pink-500 transition-colors">
            {busy ? <Loader2 className="w-6 h-6 animate-spin"/> : <Upload className="w-6 h-6"/>}
            <span className="text-[11px] font-bold uppercase tracking-wider">{busy ? "Uploading…" : "Upload image"}</span>
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPick}/>
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-1.5">{hint}</div>}
    </div>
  );
}
